import { useQuery } from '@tanstack/react-query';
import { fetchEngagementSnapshots } from '../services/outreach/engagementAnalyticsService';
import type { EnhancedMessageTrackingData } from '../services/outreach/types';
import {
  EngagementTimeRange,
  createRange,
  getClickedTimestamp,
  getOpenedTimestamp,
  getDaysFromRange,
  isWithinRange,
  normalizeRecipient,
  roundToTwo,
} from '../utils/engagementAnalytics';

interface RecipientAccumulator {
  recipient: string;
  opens: number;
  clicks: number;
  lastInteraction: number;
}

interface GeographicAccumulator {
  country: string;
  region?: string;
  city?: string;
  total: number;
  opens: number;
  clicks: number;
}

export interface RecipientEngagement {
  recipient: string;
  opens: number;
  clicks: number;
  total: number;
  lastInteraction: string | null;
}

export interface GeographicEngagement {
  country: string;
  region?: string;
  city?: string;
  total: number;
  opens: number;
  clicks: number;
}

export interface DeviceEngagement {
  label: string;
  count: number;
  percentage: number;
}

export interface EngagementInsightsData {
  recipients: RecipientEngagement[];
  geography: GeographicEngagement[];
  devices: DeviceEngagement[];
  clients: DeviceEngagement[];
  totalEngagements: number;
  lastUpdated: string;
}

const buildEngagementInsights = async (
  timeRange: EngagementTimeRange
): Promise<EngagementInsightsData> => {
  const snapshots = await fetchEngagementSnapshots({
    includeStats: false,
    includeTracking: true,
  });

  const records: EnhancedMessageTrackingData[] = snapshots.flatMap(
    (snapshot) => snapshot.tracking ?? []
  );

  const days = getDaysFromRange(timeRange);
  const { start, end } = createRange(days);

  const recipientMap = new Map<string, RecipientAccumulator>();
  const geoMap = new Map<string, GeographicAccumulator>();
  const deviceMap = new Map<string, number>();
  const clientMap = new Map<string, number>();

  records.forEach((record) => {
    const openedTimestamp = getOpenedTimestamp(record);
    const clickedTimestamp = getClickedTimestamp(record);
    const openedInRange = isWithinRange(openedTimestamp, start, end);
    const clickedInRange = isWithinRange(clickedTimestamp, start, end);

    if (!openedInRange && !clickedInRange) {
      return;
    }

    const recipient = normalizeRecipient(record.recipient, record.id);
    const accumulator = recipientMap.get(recipient) ?? {
      recipient,
      opens: 0,
      clicks: 0,
      lastInteraction: 0,
    };

    if (openedInRange) {
      accumulator.opens += 1;
      accumulator.lastInteraction = Math.max(
        accumulator.lastInteraction,
        openedTimestamp ?? 0
      );
    }

    if (clickedInRange) {
      accumulator.clicks += 1;
      accumulator.lastInteraction = Math.max(
        accumulator.lastInteraction,
        clickedTimestamp ?? 0
      );
    }

    recipientMap.set(recipient, accumulator);

    const engagementsForRecord = (openedInRange ? 1 : 0) + (clickedInRange ? 1 : 0);
    const geoKeyParts = [
      record.geolocation?.country ?? 'Unknown',
      record.geolocation?.region ?? '',
      record.geolocation?.city ?? '',
    ];
    const geoKey = geoKeyParts.join('|');
    const geoAccumulator = geoMap.get(geoKey) ?? {
      country: geoKeyParts[0],
      region: geoKeyParts[1] || undefined,
      city: geoKeyParts[2] || undefined,
      total: 0,
      opens: 0,
      clicks: 0,
    };

    geoAccumulator.total += engagementsForRecord;
    geoAccumulator.opens += openedInRange ? 1 : 0;
    geoAccumulator.clicks += clickedInRange ? 1 : 0;
    geoMap.set(geoKey, geoAccumulator);

    const deviceLabel = record.clientInfo?.device || 'Unknown Device';
    deviceMap.set(
      deviceLabel,
      (deviceMap.get(deviceLabel) ?? 0) + engagementsForRecord
    );

    const clientLabel = record.clientInfo?.name || 'Unknown Client';
    clientMap.set(
      clientLabel,
      (clientMap.get(clientLabel) ?? 0) + engagementsForRecord
    );
  });

  const allRecipients = Array.from(recipientMap.values());
  const totalEngagements = allRecipients.reduce(
    (sum, entry) => sum + entry.opens + entry.clicks,
    0
  );

  const recipientData: RecipientEngagement[] = allRecipients
    .map((recipient) => ({
      recipient: recipient.recipient,
      opens: recipient.opens,
      clicks: recipient.clicks,
      total: recipient.opens + recipient.clicks,
      lastInteraction: recipient.lastInteraction
        ? new Date(recipient.lastInteraction).toISOString()
        : null,
    }))
    .sort((a, b) => b.total - a.total)
    .slice(0, 10);

  const buildShare = (map: Map<string, number>): DeviceEngagement[] =>
    Array.from(map.entries())
      .map(([label, count]) => ({
        label,
        count,
        percentage:
          totalEngagements > 0
            ? roundToTwo((count / totalEngagements) * 100)
            : 0,
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 6);

  const geography: GeographicEngagement[] = Array.from(geoMap.values())
    .sort((a, b) => b.total - a.total)
    .slice(0, 8);

  return {
    recipients: recipientData,
    geography,
    devices: buildShare(deviceMap),
    clients: buildShare(clientMap),
    totalEngagements,
    lastUpdated: new Date().toISOString(),
  };
};

export const useEngagementInsights = (
  timeRange: EngagementTimeRange = '30d'
) =>
  useQuery({
    queryKey: ['engagement-insights', timeRange],
    queryFn: () => buildEngagementInsights(timeRange),
    staleTime: 60 * 1000,
    refetchInterval: 2 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    placeholderData: (previousData) => previousData,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
